import { useState, useMemo, useCallback } from "react";
import { useLocation } from "wouter";
import useSWR from "swr";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@/components/ui/select'
import { ChartTooltip } from '@/components/ui/chart'
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid } from 'recharts'
import { ArrowDownUp, ChevronLeft, CircleDollarSign, CreditCard, Coins, HandCoins, ListFilter, Loader2, Wallet } from 'lucide-react'
import { Transaction, TRANSACTION_TYPES, TransactionType } from '@/constants'
import { useUser } from '@/hooks/use-user'
import { apiFetch } from '@/utils'
import TransactionDetailDialog from '@/components/TransactionDetailDialog'
import CardBrandIcon from '@/components/CardBrandIcon'

export default function TransactionsPage() {
  const [, navigate] = useLocation();
  const [dateRange, setDateRange] = useState("all");
  const [transactionType, setTransactionType] = useState<
    TransactionType | "all"
  >("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const { uid } = useUser();

  const fetchUserTransactions = useCallback(
    (url: string) =>
      apiFetch(url, {
        withCredentials: true,
      }).then(async (res) => {
        const json = await res.json();
        // Support paginated response shape { data: { items: [...] } }
        if (json?.data?.items) return json.data.items;
        // Fallback: flat array
        return json.data ?? json;
      }),
    []
  );
  const { data: transactions, isLoading } = useSWR<Transaction[]>(
    uid ? `/v1/transactions/${uid}` : null,
    (url: string) => fetchUserTransactions(url),
    { revalidateOnMount: true, revalidateOnFocus: true }
  );

  // Filter transactions based on current filters
  const filteredTransactions = useMemo(() => {
    if (!transactions) return [];

    return transactions
      .filter((tx) => {
        const matchesType =
          transactionType === "all" || transactionType === tx.type;

        const matchesSearch =
          searchQuery === "" ||
          (tx.toAddress ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
          (tx.fromAddress ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
          tx.orderId?.toLowerCase().includes(searchQuery.toLowerCase());

        const txDate = new Date(tx.timestamp);
        const now = new Date();
        let matchesDate = true;

        switch (dateRange) {
          case "today":
            matchesDate = txDate.toDateString() === now.toDateString();
            break;
          case "week":
            const weekAgo = new Date();
            weekAgo.setDate(weekAgo.getDate() - 7);
            matchesDate = txDate >= weekAgo;
            break;
          case "month":
            const monthAgo = new Date();
            monthAgo.setMonth(monthAgo.getMonth() - 1);
            matchesDate = txDate >= monthAgo;
            break;
        }

        return matchesType && matchesSearch && matchesDate;
      })
      .sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
  }, [transactions, dateRange, transactionType, searchQuery]);

  // Calculate statistics split by currency
  const stats = useMemo(() => {
    if (!filteredTransactions.length) return { sechTotal: 0, usdTotal: 0, sechCount: 0, usdCount: 0, sechAvg: 0, usdAvg: 0 };

    const sechTxs = filteredTransactions.filter((tx) => tx.paymentMethod !== 'CARD');
    const usdTxs = filteredTransactions.filter((tx) => tx.paymentMethod === 'CARD');

    const sechTotal = sechTxs.reduce((sum, tx) => sum + parseFloat(tx.amount.toString()), 0);
    const usdTotal = usdTxs.reduce((sum, tx) => sum + parseFloat(tx.amount.toString()), 0);

    return {
      sechTotal,
      usdTotal,
      sechCount: sechTxs.length,
      usdCount: usdTxs.length,
      sechAvg: sechTxs.length ? sechTotal / sechTxs.length : 0,
      usdAvg: usdTxs.length ? usdTotal / usdTxs.length : 0,
    };
  }, [filteredTransactions]);

  // Prepare chart data split by currency
  const chartData = useMemo(() => {
    if (!filteredTransactions.length) return [];

    const grouped: Record<string, { sech: number; usd: number }> = {};
    for (const tx of filteredTransactions) {
      const date = new Date(tx.timestamp).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
      if (!grouped[date]) grouped[date] = { sech: 0, usd: 0 };
      const amount = parseFloat(tx.amount.toString());
      if (tx.paymentMethod === 'CARD') {
        grouped[date].usd += amount;
      } else {
        grouped[date].sech += amount;
      }
    }

    return Object.entries(grouped).map(([date, { sech, usd }]) => ({
      date,
      sech,
      usd,
    }));
  }, [filteredTransactions]);

  const getTransactionTypeOptions = () =>
    Object.entries(TRANSACTION_TYPES) as [TransactionType | "all", string][];


  return (
    <div className="min-h-auto bg-[#18181B]">
      <div className="max-w-5xl mx-auto px-5 pb-8">
        {/* Header with back button */}
        <div
			role="banner"
			className="
				sticky
				top-0
				left-[20px]
				z-10
				bg-[#181b1b]
				pb-[12px]
				backdrop-blur-[8px]
				mb-[12px]
				h-auto
				flex
				items-center
				justify-start
				gap-2
				text-white
				py-3
			"
			>
					<button
						type="button"
						aria-label="Go back"
						onClick={() => navigate('/')}
						className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
					>
						<ChevronLeft className='h-[22px] w-[22px] text-[#ffffff]'/>
					</button>

					<h1 className="text-[20px] leading-[18px] font-bold font-montserrat tracking-wide">
            Transactions
					</h1>
				</div>
        {/* Page Header */}
        <div className="text-start mb-3">
          <h1 className="text-[16px] leading-[18px] font-semibold font-figtree text-[#ffffff] mb-2">
            Transaction Reports
          </h1>
          <p className="text-[13px] leading-[15px] font-normal text-[#BABABB]">
            View and analyze your transaction history
          </p>
        </div>

        <div
          className="w-full rounded-[8px] px-3 py-4
            bg-gradient-to-r from-[#1D0C50] via-[#1E1534] to-[#141026]
            flex items-center justify-between
            shadow-lg relative overflow-hidden mb-2"
        >
          <div className="bg-[linear-gradient(270deg,rgba(255,255,255,0.92)_0%,rgba(20,16,38,0.92)_100%)] !rotate-[23deg] absolute top-[-256px] right-[74px] h-[304px] w-[18px] blur-[20px]"></div>
          <div className="bg-[linear-gradient(270deg,rgba(255,255,255,0.92)_0%,rgba(20,16,38,0.92)_100%)] !rotate-[23deg] absolute bottom-[20px] right-[36px] h-[304px] w-[18px] blur-[10px]"></div>
          <div className="bg-[linear-gradient(270deg,rgba(255,255,255,0.92)_0%,rgba(20,16,38,0.92)_100%)] !rotate-[23deg] absolute bottom-[24px] right-[-14px] h-[304px] w-[18px] blur-[15px] "></div>

          <span className="dot top-[18%] left-[12%]"></span>
          <span className="dot top-[32%] left-[88%]"></span>
          <span className="dot top-[22%] left-[48%]"></span>
          <span className="dot top-[40%] left-[22%]"></span>
          <span className="dot top-[18%] left-[75%]"></span>
          <span className="dot top-[72%] left-[20%]"></span>
          <span className="dot top-[60%] left-[38%]"></span>
          <span className="dot top-[48%] left-[55%]"></span>
          <span className="dot top-[85%] left-[72%]"></span>

          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-[6px] bg-white/10 flex items-center justify-center">
              <HandCoins className="h-6 w-6 text-[#ffffff]" />
            </div>

            <span className="text-[#ffffff] text-sm max-[450px]:text-[12px] leading-[20px] font-medium font-figtree">
              Total Volume
            </span>
          </div>

          <div className="flex flex-col items-end gap-0.5">
            {stats.sechTotal > 0 && (
              <div className="bg-white/10 backdrop-blur-md px-[14px] max-[450px]:px-[8px] py-1 rounded-[6px] text-[#ffffff] text-[22px] max-[450px]:text-[14px] font-figtree font-semibold tracking-wide">
                {stats.sechTotal.toLocaleString('en-US', { minimumFractionDigits: 6, maximumFractionDigits: 6 })}{" "}
                <span className="text-sm max-[450px]:text-[10px] font-semibold font-figtree">SECH</span>
              </div>
            )}
            {stats.usdTotal > 0 && (
              <div className="bg-white/10 backdrop-blur-md px-[14px] max-[450px]:px-[8px] py-1 rounded-[6px] text-[#ffffff] text-[18px] max-[450px]:text-[12px] font-figtree font-semibold tracking-wide">
                ${stats.usdTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{" "}
                <span className="text-sm max-[450px]:text-[10px] font-semibold font-figtree">USD</span>
              </div>
            )}
          </div>

          {/* <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none"></div> */}
        </div>

        <div
          className="w-full rounded-[8px] px-3 py-4
            bg-gradient-to-r from-[#0C3950] via-[#153422] to-[#1D1026]
            flex items-center justify-between
            shadow-lg relative overflow-hidden mb-2"
        >
          <div className="bg-[linear-gradient(270deg,rgba(255,255,255,0.92)_0%,rgba(20,16,38,0.92)_100%)] !rotate-[23deg] absolute top-[-256px] right-[74px] h-[304px] w-[18px] blur-[20px]"></div>
          <div className="bg-[linear-gradient(270deg,rgba(255,255,255,0.92)_0%,rgba(20,16,38,0.92)_100%)] !rotate-[23deg] absolute bottom-[20px] right-[36px] h-[304px] w-[18px] blur-[10px]"></div>
          <div className="bg-[linear-gradient(270deg,rgba(255,255,255,0.92)_0%,rgba(20,16,38,0.92)_100%)] !rotate-[23deg] absolute bottom-[24px] right-[-14px] h-[304px] w-[18px] blur-[15px] "></div>

          <span className="dot top-[18%] left-[12%]"></span>
          <span className="dot top-[32%] left-[88%]"></span>
          <span className="dot top-[22%] left-[48%]"></span>
          <span className="dot top-[40%] left-[22%]"></span>
          <span className="dot top-[18%] left-[75%]"></span>
          <span className="dot top-[72%] left-[20%]"></span>
          <span className="dot top-[60%] left-[38%]"></span>
          <span className="dot top-[48%] left-[55%]"></span>
          <span className="dot top-[85%] left-[72%]"></span>

          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-[6px] bg-white/10 flex items-center justify-center">
              <Coins className="h-6 w-6 text-[#ffffff]" />
            </div>

            <span className="text-[#ffffff] text-sm max-[450px]:text-[12px] leading-[20px] font-medium font-figtree">
              Transaction Count
            </span>
          </div>

          <div className="flex flex-col items-end gap-0.5">
            {stats.sechCount > 0 && (
              <div className="bg-white/10 backdrop-blur-md px-[14px] max-[450px]:px-[8px] py-1 rounded-[6px] text-[#ffffff] text-[22px] max-[450px]:text-[14px] font-figtree font-semibold tracking-wide">
                {stats.sechCount}{" "}
                <span className="text-sm max-[450px]:text-[10px] font-semibold font-figtree">Token</span>
              </div>
            )}
            {stats.usdCount > 0 && (
              <div className="bg-white/10 backdrop-blur-md px-[14px] max-[450px]:px-[8px] py-1 rounded-[6px] text-[#ffffff] text-[18px] max-[450px]:text-[12px] font-figtree font-semibold tracking-wide">
                {stats.usdCount}{" "}
                <span className="text-sm max-[450px]:text-[10px] font-semibold font-figtree">Card</span>
              </div>
            )}
          </div>

          {/* <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none"></div> */}
        </div>

        <div
          className="w-full rounded-[8px] mb-6 px-3 py-4
            bg-gradient-to-r from-[#4D0C50] via-[#4E2318] to-[#261F10]
            flex items-center justify-between
            shadow-lg relative overflow-hidden"
        >
          <div className="bg-[linear-gradient(270deg,rgba(255,255,255,0.92)_0%,rgba(20,16,38,0.92)_100%)] !rotate-[23deg] absolute top-[-256px] right-[74px] h-[304px] w-[18px] blur-[20px]"></div>
          <div className="bg-[linear-gradient(270deg,rgba(255,255,255,0.92)_0%,rgba(20,16,38,0.92)_100%)] !rotate-[23deg] absolute bottom-[20px] right-[36px] h-[304px] w-[18px] blur-[10px]"></div>
          <div className="bg-[linear-gradient(270deg,rgba(255,255,255,0.92)_0%,rgba(20,16,38,0.92)_100%)] !rotate-[23deg] absolute bottom-[24px] right-[-14px] h-[304px] w-[18px] blur-[15px] "></div>

          <span className="dot top-[18%] left-[12%]"></span>
          <span className="dot top-[32%] left-[88%]"></span>
          <span className="dot top-[22%] left-[48%]"></span>
          <span className="dot top-[40%] left-[22%]"></span>
          <span className="dot top-[18%] left-[75%]"></span>
          <span className="dot top-[72%] left-[20%]"></span>
          <span className="dot top-[60%] left-[38%]"></span>
          <span className="dot top-[48%] left-[55%]"></span>
          <span className="dot top-[85%] left-[72%]"></span>

          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-[6px] bg-white/10 flex items-center justify-center">
              <CircleDollarSign className="h-6 w-6 text-[#ffffff]" />
            </div>

            <span className="text-[#ffffff] text-sm max-[450px]:text-[12px] leading-[20px] font-medium font-figtree">
              Average Amount
            </span>
          </div>

          <div className="flex flex-col items-end gap-0.5">
            {stats.sechAvg > 0 && (
              <div className="bg-white/10 backdrop-blur-md px-[14px] max-[450px]:px-[8px] py-1 rounded-[6px] text-[#ffffff] text-[22px] max-[450px]:text-[14px] font-figtree font-semibold tracking-wide">
                {stats.sechAvg.toLocaleString('en-US', { minimumFractionDigits: 6, maximumFractionDigits: 6 })}{" "}
                <span className="text-sm max-[450px]:text-[10px] font-semibold font-figtree">SECH</span>
              </div>
            )}
            {stats.usdAvg > 0 && (
              <div className="bg-white/10 backdrop-blur-md px-[14px] max-[450px]:px-[8px] py-1 rounded-[6px] text-[#ffffff] text-[18px] max-[450px]:text-[12px] font-figtree font-semibold tracking-wide">
                ${stats.usdAvg.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{" "}
                <span className="text-sm max-[450px]:text-[10px] font-semibold font-figtree">USD</span>
              </div>
            )}
          </div>

          {/* <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none"></div> */}
        </div>

        <div className="flex flex-col gap-6">
          {/* Chart */}
          <Card className="px-3 py-4 bg-[#2F2F32] border-0 rounded-[16px] mb-6">
            <h2 className="text-sm leading-4 font-medium font-figtree mb-4 text-[#ffffff]">
              Transaction Volume
            </h2>
            <div className="w-full">
              <div style={{ width: '100%', height: 300 }}>
                {!chartData?.length ? (
                  <div className="h-full w-full flex items-center justify-center flex-col gap-2">
                    <span className="empty-icon h-[42px] w-[42px] flex items-center justify-center rounded-full bg-[rgba(255,255,255,0.1)]" aria-hidden="true">
                      <ArrowDownUp className='h-[22p) : (x] w-[22px] text-[#ffffff]' />
                    </span>
        
                    <p className='text-xs leading-4 font-medium text-[#ffffff] font-figtree'>No transactions yet</p>
                  </div>
                  ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={chartData}
                      margin={{ top: 20, right: 40 }}
                      barCategoryGap={6}
                    >
                      <CartesianGrid
                        stroke="rgba(104, 104, 104, 1)"
                        horizontal
                        vertical={false}
                        strokeDasharray="3 3"
                      />
                      <XAxis
                        dataKey="date"
                        tickLine={false}
                        axisLine={false}
                        stroke="#BABABB"
                        fontSize={11}
                        height={50}
                        fontWeight={600}
                        angle={-35}
                        textAnchor="end"
                      />

                      <YAxis
                        yAxisId="sech"
                        tickLine={false}
                        axisLine={false}
                        stroke="#ef4444"
                        fontSize={11}
                        fontWeight={600}
                        tickFormatter={(v) => `${v.toFixed(2)}`}
                        label={{ value: 'SECH', angle: -90, position: 'insideLeft', fill: '#ef4444', fontSize: 11 }}
                      />
                      <YAxis
                        yAxisId="usd"
                        orientation="right"
                        tickLine={false}
                        axisLine={false}
                        stroke="#3b82f6"
                        fontSize={11}
                        fontWeight={600}
                        tickFormatter={(v) => `$${v.toFixed(0)}`}
                        label={{ value: 'USD', angle: 90, position: 'insideRight', fill: '#3b82f6', fontSize: 11 }}
                      />

                      <Bar yAxisId="sech" dataKey="sech" fill="rgba(220, 38, 38, 0.6)" name="SECH" />
                      <Bar yAxisId="usd" dataKey="usd" fill="rgba(59, 130, 246, 0.6)" name="USD" />
                      <ChartTooltip
                        cursor={{ fill: "rgba(255,255,255,0.1)" }}
                        content={({ active, payload }) => {
                          if (!active || !payload?.length) return null;
                          const data = payload[0].payload;
                          return (
                            <div className="rounded-lg border border-[#4C4C4C] bg-[#2F2F32] p-2 shadow-sm">
                              <div className="flex flex-col gap-1.5">
                                <span className="font-bold text-[#ffffff] text-[0.80rem]">
                                  {data.date}
                                </span>
                                {data.sech > 0 && (
                                  <div className="flex items-center gap-1.5">
                                    <span className="h-2 w-2 rounded-full bg-red-500 shrink-0" />
                                    <span className="text-[#ffffff] text-[0.75rem]">
                                      {Number(data.sech).toLocaleString('en-US', { minimumFractionDigits: 6, maximumFractionDigits: 6 })} SECH
                                    </span>
                                  </div>
                                )}
                                {data.usd > 0 && (
                                  <div className="flex items-center gap-1.5">
                                    <span className="h-2 w-2 rounded-full bg-blue-500 shrink-0" />
                                    <span className="text-[#ffffff] text-[0.75rem]">
                                      ${Number(data.usd).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        }}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </Card>

					{/* <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none"></div> */}
				</div>


				

				<div className="flex flex-col gap-2">
					{/* Filters */}
					<div className="flex flex-col sm:flex-row gap-2 sm:items-center justify-between">
						<div className="flex items-center justify-between w-full">
							<Input
								placeholder="Search by address or order ID..."
								className="flex-none h-[48px] rounded-[8px] !bg-[rgba(0,0,0,0.1)] border border-[rgba(255,255,255,0.12)] bg-[#3C3C3C] text-[#ffffff] placeholder:text-[#BABABB] placeholder:text-[13px] placeholder:leading-[18px] placeholder:font-normal focus-visible:outline-none
                          focus-visible:ring-0
                          focus-visible:ring-offset-0"
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
							/>
						</div>
						<Select value={dateRange} onValueChange={setDateRange}>
							<SelectTrigger className="flex-1 focus:outline-none focus:ring-0 focus:ring-offset-0 sm:w-[180px] h-[42px] rounded-[8px] !bg-[rgba(0,0,0,0.1)] border border-[rgba(255,255,255,0.12)] sm:flex-none bg-[#3C3C3C] text-[#BABABB]">
								<SelectValue placeholder="Select date range" />
							</SelectTrigger>
							<SelectContent className="bg-[#2F2F32] border-0">
								<SelectItem value="all" className="text-[#AFAFAF] h-[42px] focus:!bg-[#212124] focus:text-[#ffffff]">All Time</SelectItem>
								<SelectItem value="today" className="text-[#AFAFAF] h-[42px] focus:!bg-[#212124] focus:text-[#ffffff]">Today</SelectItem>
								<SelectItem value="week" className="text-[#AFAFAF] h-[42px] focus:!bg-[#212124] focus:text-[#ffffff]">Last 7 Days</SelectItem>
								<SelectItem value="month" className="text-[#AFAFAF] h-[42px] focus:!bg-[#212124] focus:text-[#ffffff]">Last 30 Days</SelectItem>
							</SelectContent>
						</Select>
						<Select
							value={transactionType}
							onValueChange={(value: TransactionType | 'all') =>
								setTransactionType(value)
							}>
							<SelectTrigger className="flex-1 sm:w-[180px] focus:outline-none focus:ring-0 focus:ring-offset-0 h-[42px] rounded-[8px] sm:flex-none !bg-[rgba(0,0,0,0.1)] border border-[rgba(255,255,255,0.12)] bg-[#3C3C3C] text-[#BABABB]">
								<SelectValue placeholder="Select type" />
							</SelectTrigger>
							<SelectContent className="bg-[#2F2F32] border-0">
								{getTransactionTypeOptions().map(([type, label]) => (
									<SelectItem key={type} value={type} className="text-[#AFAFAF] h-[42px] focus:!bg-[#212124] focus:text-[#ffffff]">
										{label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					{/* Transactions — Mobile cards */}
					<div className="md:hidden flex flex-col gap-2">
						{isLoading ? (
							<div className="flex items-center justify-center h-40 gap-2">
								<Loader2 className="h-6 w-6 animate-spin text-primary" />
								<p className="text-md text-[#BABABB]">Fetching transactions data...</p>
							</div>
						) : filteredTransactions?.length ? (
							filteredTransactions.map((tx) => (
								<div
									key={tx.id}
									className="bg-[#212124] rounded-[8px] p-3 cursor-pointer hover:bg-[rgba(255,255,255,0.05)] transition-colors"
									onClick={() => setSelectedTransaction(tx)}
								>
									<div className="flex items-center justify-between mb-1.5">
										<span className="text-[#BABABB] text-[11px] font-figtree">
											{new Date(tx.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
										</span>
										{tx.paymentMethod === 'CARD' ? (
											<span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-400">
												<CreditCard className="h-2.5 w-2.5" />Card
											</span>
										) : (
											<span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-orange-500/15 text-orange-400">
												<Wallet className="h-2.5 w-2.5" />Token
											</span>
										)}
									</div>
									<div className="flex items-center justify-between">
										<span className="text-[#C7C7C7] text-xs font-medium font-figtree">{TRANSACTION_TYPES[tx.type]}</span>
										<span className="text-[#ffffff] text-[13px] font-semibold font-figtree">
											{tx.paymentMethod === 'CARD'
												? `$${parseFloat(tx.amount.toString()).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
												: `${parseFloat(tx.amount.toString()).toLocaleString('en-US', { minimumFractionDigits: 6, maximumFractionDigits: 6 })} SECH`
											}
										</span>
									</div>
								</div>
							))
						) : (
							<div className="flex items-center justify-center h-40 gap-2 flex-col">
								<span className="empty-icon h-[42px] w-[42px] flex items-center justify-center rounded-full bg-[rgba(255,255,255,0.1)]" aria-hidden="true">
									<ArrowDownUp className='h-[22px] w-[22px] text-[#ffffff]' />
								</span>
								<p className='text-xs leading-4 font-medium text-[#ffffff] font-figtree'>No transactions found</p>
							</div>
						)}
					</div>

					{/* Transactions — Desktop table */}
					<Card className="hidden md:block bg-[#3C3C3C] rounded-[8px] border-0 overflow-x-auto">
						<Table className="min-w-[710px] bg-[#212124]">
							<TableHeader className='bg-[#3E3E43]'>
								<TableRow className="!border-0 hover:bg-transparent">
									<TableHead className="text-[#ffffff] h-[38px] text-[13px] leading-[18px] font-medium font-figtree">Date & Time</TableHead>
									<TableHead className="text-[#ffffff] h-[38px] text-[13px] leading-[18px] font-medium font-figtree">Type</TableHead>
									<TableHead className="text-[#ffffff] h-[38px] text-[13px] leading-[18px] font-medium font-figtree">Method</TableHead>
									<TableHead className="text-[#ffffff] h-[38px] text-[13px] leading-[18px] font-medium font-figtree">Paid With</TableHead>
									<TableHead className="text-[#ffffff] h-[38px] text-[13px] leading-[18px] font-medium font-figtree">Amount</TableHead>
									<TableHead className="text-[#ffffff] h-[38px] text-[13px] leading-[18px] font-medium font-figtree">Order ID</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{isLoading ? (
									<TableRow key="dataLoading" className="hover:bg-transparent">
										<TableCell colSpan={6} className="text-center h-40">
											<div className="flex flex-row items-center justify-center gap-2">
												<Loader2 className="h-6 w-6 animate-spin text-primary" />
												<p className="text-md text-[#BABABB]">
													Fetching transactions data...
												</p>
											</div>
										</TableCell>
									</TableRow>
								) : filteredTransactions?.length ? (
									filteredTransactions.map((tx) => (
										<TableRow
											key={tx.id}
											className="border-b border-[#4C4C4C] hover:bg-[rgba(255,255,255,0.05)] cursor-pointer"
											onClick={() => setSelectedTransaction(tx)}
										>
											<TableCell className="text-[#C7C7C7] px-3 py-0 h-[38px] text-xs leading-4 font-medium font-figtree">
												{new Date(tx.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
											</TableCell>
											<TableCell className="text-[#C7C7C7] px-3 py-0 h-[38px] text-xs leading-4 font-medium font-figtree">{TRANSACTION_TYPES[tx.type]}</TableCell>
											<TableCell className="text-[#C7C7C7] px-3 py-0 h-[38px] text-xs leading-4 font-medium font-figtree">
												{tx.paymentMethod === 'CARD' ? (
													<span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-400">
														<CreditCard className="h-2.5 w-2.5" />Card
													</span>
												) : (
													<span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-orange-500/15 text-orange-400">
														<Wallet className="h-2.5 w-2.5" />Token
													</span>
												)}
											</TableCell>
											<TableCell className="text-[#C7C7C7] px-3 py-0 h-[38px] text-xs leading-4 font-medium font-figtree">
												{tx.paymentMethod === 'CARD' ? (
													tx.paymentDetails?.last4 ? (
														<span className="inline-flex items-center gap-1.5">
															<CardBrandIcon brand={tx.paymentDetails?.brand} width={28} />
															<span>••••{tx.paymentDetails.last4}</span>
														</span>
													) : '—'
												) : (
													tx.fromAddress ? (
														<span className="inline-flex items-center gap-1.5">
															<Wallet className="h-3 w-3 text-orange-400 shrink-0" />
															<span>{`${tx.fromAddress.slice(0, 6)}...${tx.fromAddress.slice(-4)}`}</span>
														</span>
													) : '—'
												)}
											</TableCell>
											<TableCell className="text-[#C7C7C7] px-3 py-0 h-[38px] text-xs leading-4 font-medium font-figtree">
												{tx.paymentMethod === 'CARD'
													? `$${parseFloat(tx.amount.toString()).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
													: `${parseFloat(tx.amount.toString()).toLocaleString('en-US', { minimumFractionDigits: 6, maximumFractionDigits: 6 })} SECH`
												}
											</TableCell>
											<TableCell className="text-[#C7C7C7] px-3 py-0 h-[38px] text-xs leading-4 font-medium font-figtree">{tx.orderId || '-'}</TableCell>
										</TableRow>
									))
								) : (
									<TableRow key="dataLoading" className="hover:bg-transparent">
										<TableCell colSpan={6} className="text-center h-40">
											<div className="flex items-center justify-center gap-2 flex-col">
												<span className="empty-icon h-[42px] w-[42px] flex items-center justify-center rounded-full bg-[rgba(255,255,255,0.1)]" aria-hidden="true">
													<ArrowDownUp className='h-[22px] w-[22px] text-[#ffffff]' />
												</span>
												<p className='text-xs leading-4 font-medium text-[#ffffff] font-figtree'>No transactions found</p>
											</div>
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</Card>
				</div>

			<TransactionDetailDialog
				transaction={selectedTransaction}
				open={selectedTransaction !== null}
				onOpenChange={(open) => { if (!open) setSelectedTransaction(null) }}
			/>
			</div>
		</div>
	)
}
